import { useEffect, useMemo, useState } from 'react';
import { Drawer } from '../../components/data';
import { ErrorBanner, Field, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { formatNumber } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  OnboardingOption,
  OnboardingTemplate,
  SalesAccount,
  SalesOpportunity,
} from '../../lib/types';
import {
  BLANK_ANSWERS,
  ContactFields,
  LocationFields,
  TechnicalFields,
  type AnswerForm,
} from './OnboardingFields';
import { INDUSTRIES, TIMELINE_LABEL } from './onboardingMeta';

/**
 * The intake wizard — the front half of the standalone onboarding app.
 *
 * Five steps, in the source app's order: who they are, who we talk to, what
 * they bought, what they run, and a review. What is different here is the
 * first step. The source app collected a company name into its own `clients`
 * collection; this one attaches the onboarding to a sales account, because the
 * company already exists in `core.Client` and a second copy of it is exactly
 * what the handover at the other end cannot recover from.
 *
 * The review step lists the tasks the answers WILL create, before they are
 * created. In the source app that list only appeared afterwards, which made a
 * mis-ticked service eleven tasks to delete by hand.
 */

/**
 * The account's answers, copied into the ones this wizard has not been given.
 *
 * City, region and seat count are already on the sales account — they were
 * asked for when it was created — and asking again is how the same company
 * ends up with two seat counts that disagree. Only blanks are filled: an
 * answer already typed is never overwritten by changing the account.
 */
function carryOver(form: AnswerForm, account: SalesAccount | null): AnswerForm {
  if (!account) return form;
  return {
    ...form,
    address: form.address || (account.Address ?? ''),
    city: form.city || (account.City ?? ''),
    region: form.region || (account.Region ?? ''),
    postalCode: form.postalCode || (account.PostalCode ?? ''),
    /* Supported users is the same figure as seats, under the name each desk
       uses for it. It sizes the onboarding, so having it up front is worth
       more than the purity of asking twice. */
    userCount: form.userCount || (account.Seats === null ? '' : String(account.Seats)),
    /* The account's primary contact. The same person, asked for twice within a
       minute otherwise — and the copy stored on the onboarding is still a copy,
       so correcting it here does not reach back into the sales record. */
    contactFirstName: form.contactFirstName || (account.PrimaryContactFirstName ?? ''),
    contactLastName: form.contactLastName || (account.PrimaryContactLastName ?? ''),
    contactTitle: form.contactTitle || (account.PrimaryContactTitle ?? ''),
    contactEmail: form.contactEmail || (account.PrimaryContactEmail ?? ''),
    contactPhone: form.contactPhone || (account.PrimaryContactPhone ?? ''),
  };
}

/** A company found in Autotask, and the one picked with its details. */
interface AtCompanyHit {
  id: number;
  name: string;
  city: string;
  state: string;
}
interface AtCompany {
  id: number;
  name: string;
  address: string;
  city: string;
  region: string;
  postalCode: string;
  phone: string;
  website: string;
  contact: AtContact | null;
  contacts: AtContact[];
}
interface AtContact {
  id: number;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  primary: boolean;
}

/**
 * The Autotask company's answers in place of the form's. Picking a company is
 * a deliberate choice of where the details come from, so its address replaces
 * what is there; the contact only fills blanks, because the person running the
 * onboarding is often not the one Autotask marks primary.
 */
function fromAutotask(form: AnswerForm, c: AtCompany): AnswerForm {
  return {
    ...form,
    address: c.address || form.address,
    city: c.city || form.city,
    region: c.region || form.region,
    postalCode: c.postalCode || form.postalCode,
    contactFirstName: form.contactFirstName || (c.contact?.firstName ?? ''),
    contactLastName: form.contactLastName || (c.contact?.lastName ?? ''),
    contactTitle: form.contactTitle || (c.contact?.title ?? ''),
    contactEmail: form.contactEmail || (c.contact?.email ?? ''),
    contactPhone: form.contactPhone || (c.contact?.phone ?? '') || c.phone,
  };
}

/** A service on the company's active Autotask contracts — the fields AM's Services tab shows. */
interface ContractService {
  id: number;
  contractId: number;
  contract: string;
  service: string;
  period: string | null;
  units: number | null;
  unitPrice: number | null;
  unitCost: number | null;
  total: number | null;
}
interface ContractInfo {
  id: number;
  name: string;
  number: string | null;
  type: string | null;
  start: string | null;
  end: string | null;
}

type StepKey = 'company' | 'contact' | 'services' | 'technical' | 'review';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'company', label: 'Company' },
  { key: 'contact', label: 'Contact' },
  { key: 'services', label: 'Services' },
  { key: 'technical', label: 'Environment' },
  { key: 'review', label: 'Review' },
];

interface CompanyForm {
  accountId: number | '' | 'new';
  companyName: string;
  industry: string;
  website: string;
  opportunityId: number | '';
}

export function OnboardingWizard({
  accounts,
  deals,
  defaultAccountId,
  onClose,
}: {
  accounts: SalesAccount[];
  deals: SalesOpportunity[];
  /** Start on this account — the one just created on the Sales tab. */
  defaultAccountId?: number | null;
  onClose: () => void;
}) {
  const options = useResource<OnboardingOption[]>('/sales/onboarding/options');
  const templates = useResource<OnboardingTemplate[]>('/sales/onboarding/templates');

  const [step, setStep] = useState(0);
  const [company, setCompany] = useState<CompanyForm>({
    accountId: defaultAccountId ?? '',
    companyName: '',
    industry: '',
    website: '',
    opportunityId: '',
  });
  /* An account carries answers this wizard would otherwise ask for a second
     time — where they are and how many seats they have. Seeded here for the
     account the wizard opened on, and re-seeded by `chooseAccount` below when
     the account is changed. */
  const [answers, setAnswers] = useState<AnswerForm>(() =>
    carryOver(BLANK_ANSWERS, accounts.find((a) => a.AccountId === defaultAccountId) ?? null),
  );

  /* The company from Autotask: what is typed, what it found, and the one picked.
     Opened on an account from the Sales tab, the wizard starts on the account
     list instead, since the account is already known. */
  const [source, setSource] = useState<'autotask' | 'accounts'>(defaultAccountId ? 'accounts' : 'autotask');
  const [atQuery, setAtQuery] = useState('');
  const [atHits, setAtHits] = useState<AtCompanyHit[]>([]);
  const [atSearching, setAtSearching] = useState(false);
  const [atPicked, setAtPicked] = useState<AtCompany | null>(null);
  const [atLoading, setAtLoading] = useState(false);
  const [atError, setAtError] = useState<string | null>(null);

  useEffect(() => {
    const q = atQuery.trim();
    if (source !== 'autotask' || q.length < 2 || (atPicked && atPicked.name === q)) {
      setAtHits([]);
      return;
    }
    let live = true;
    /* A pause before asking, so each keystroke is not its own Autotask call. */
    const timer = window.setTimeout(() => {
      setAtSearching(true);
      setAtError(null);
      api
        .get<AtCompanyHit[]>(`/sales/onboarding/autotask/companies?${new URLSearchParams({ q })}`)
        .then((rows) => live && setAtHits(rows))
        .catch((err: unknown) => live && setAtError(err instanceof Error ? err.message : 'Could not search Autotask'))
        .finally(() => live && setAtSearching(false));
    }, 300);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [atQuery, source, atPicked]);

  /* The services on the picked company's Autotask contracts, read when the
     company is picked, so the Services step has them ready. */
  const [contract, setContract] = useState<{ services: ContractService[]; contracts: ContractInfo[] } | null>(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState<string | null>(null);
  useEffect(() => {
    setContract(null);
    setContractError(null);
    if (!atPicked) return;
    let live = true;
    setContractLoading(true);
    api
      .get<{ contracts: ContractInfo[]; services: ContractService[] }>(`/sales/onboarding/autotask/companies/${atPicked.id}/services`)
      .then((r) => live && setContract({ services: r.services, contracts: r.contracts }))
      .catch((err: unknown) => live && setContractError(err instanceof Error ? err.message : 'Could not read the contract'))
      .finally(() => live && setContractLoading(false));
    return () => {
      live = false;
    };
  }, [atPicked]);

  /* The email to the account manager: every answer in the handover, as text. */
  const [copied, setCopied] = useState(false);

  const setC = (patch: Partial<CompanyForm>) => setCompany((f) => ({ ...f, ...patch }));
  const setA = (patch: Partial<AnswerForm>) => setAnswers((f) => ({ ...f, ...patch }));

  /* Changing the account moves the answers with it, but only the ones still
     holding nothing — the same rule the deal editor follows. Somebody who has
     typed a seat count and then corrects the account keeps what they typed. */
  const chooseAccount = (value: string) => {
    const accountId = value === 'new' ? 'new' : value ? Number(value) : '';
    const picked = typeof accountId === 'number'
      ? (accounts.find((a) => a.AccountId === accountId) ?? null)
      : null;
    setCompany((f) => ({ ...f, accountId, opportunityId: '' }));
    setAnswers((f) => carryOver(f, picked));
  };

  /* Pick an Autotask company: its details fill the form, and it is matched to
     the Sales account of the same name — so a won deal still attaches — or
     else becomes a new account under that name (the API reuses a name it
     already has rather than duplicating it). */
  const pickAutotask = async (hit: AtCompanyHit) => {
    setAtHits([]);
    setAtQuery(hit.name);
    setAtLoading(true);
    setAtError(null);
    try {
      const c = await api.get<AtCompany>(`/sales/onboarding/autotask/companies/${hit.id}`);
      setAtPicked(c);
      const key = c.name.trim().toLowerCase();
      const match = accounts.find((a) => a.Name.trim().toLowerCase() === key) ?? null;
      setCompany((f) => ({
        ...f,
        accountId: match ? match.AccountId : 'new',
        companyName: match ? '' : c.name,
        website: match ? f.website : c.website || f.website,
        opportunityId: '',
      }));
      setAnswers((f) => fromAutotask(carryOver(f, match), c));
    } catch (err) {
      setAtError(err instanceof Error ? err.message : 'Could not read the company from Autotask');
    }
    setAtLoading(false);
  };

  const allOptions = options.data ?? [];
  const isNewCompany = company.accountId === 'new';
  const account = accounts.find((a) => a.AccountId === company.accountId) ?? null;
  const companyName = isNewCompany ? company.companyName.trim() : (account?.Name ?? '');

  /* Deals on the chosen account only. A picker across the whole pipeline would
     be a list of other companies' deals, which is only ever a wrong answer. */
  const accountDeals = useMemo(
    () => deals.filter((d) => d.AccountId === company.accountId),
    [deals, company.accountId],
  );

  /**
   * The tasks these answers will create, worked out the way the API works them
   * out: a rule either names a template or sweeps a whole category.
   *
   * A second implementation of the matching, knowingly. The API is the
   * authority and runs it again on submit — this copy exists only so the review
   * step can show the plan before it is committed, and it reads the same
   * trigger rules the API does, so the rules themselves cannot drift.
   */
  const planned = useMemo(() => {
    const chosen = new Set([
      ...answers.services.map((v) => `service:${v}`),
      ...answers.platforms.map((v) => `platform:${v}`),
      ...answers.operatingSystems.map((v) => `os:${v}`),
    ]);
    return (templates.data ?? []).filter((t) =>
      t.Triggers.some((r) => chosen.has(`${r.Kind}:${r.Value}`)),
    );
  }, [templates.data, answers.services, answers.platforms, answers.operatingSystems]);

  /** The handover as an email body: plain text, every section, blanks left out. */
  const handoverEmail = () => {
    const name = (code: string) => allOptions.find((o) => o.Code === code)?.Name ?? code;
    const line = (label: string, v: string | null | undefined) => (v && v.trim() ? `${label}: ${v.trim()}` : null);
    const block = (title: string, rows: (string | null)[]) => {
      const kept = rows.filter((r): r is string => Boolean(r));
      return kept.length ? [title.toUpperCase(), ...kept, ''] : [];
    };
    const deal = accountDeals.find((d) => d.OpportunityId === company.opportunityId);
    return [
      `Handover to account management: ${companyName}`,
      'You have been assigned this account. Please go into the Account Management module of MSP-OPS, import the client, and then schedule a kickoff meeting for onboarding.',
      '',
      ...block('Company', [
        line('Company', companyName),
        line('Address', [answers.address, answers.city, answers.region, answers.postalCode].filter((v) => v.trim()).join(', ')),
        line('Website', company.website || atPicked?.website),
        line('Phone', atPicked?.phone),
        line('Company size', answers.companySize),
        deal ? `Deal: ${deal.Name} — ${formatNumber(deal.Mrr, 'currency')}/mo` : null,
      ]),
      ...block('Primary contact', [
        line('Name', [answers.contactFirstName, answers.contactLastName].filter((v) => v.trim()).join(' ')),
        line('Title', answers.contactTitle),
        line('Email', answers.contactEmail),
        line('Phone', answers.contactPhone),
        line('Prefers', answers.preferredContact),
      ]),
      ...block(
        'Services on the Autotask contract',
        (contract?.services ?? []).map(
          (s) =>
            `- ${s.service}${s.units !== null ? ` × ${s.units}` : ''}${s.period ? `, ${s.period}` : ''}` +
            `${s.unitPrice !== null ? ` @ ${formatNumber(s.unitPrice, 'currency')}` : ''}` +
            `${s.total !== null ? ` = ${formatNumber(s.total, 'currency')}` : ''}${s.contract ? ` (${s.contract})` : ''}`,
        ),
      ),
      ...block('Service notes', [answers.serviceNotes.trim() || null]),
      ...block('Environment', [
        line('Users / endpoints', answers.userCount),
        line('Current IT provider', answers.currentProvider),
        line('Operating systems', answers.operatingSystems.map(name).join(', ')),
        line('Cloud platforms', answers.platforms.map(name).join(', ')),
        line('Pain points', answers.painPoints),
        line('Start timeline', answers.timeline ? TIMELINE_LABEL[answers.timeline] : ''),
      ]),
      ...block(
        `Onboarding tasks (${planned.length})`,
        planned.map((t) => `- ${t.Title}${t.DefaultDueDays ? ` — due in ${t.DefaultDueDays}d` : ''}`),
      ),
    ]
      .join('\n')
      .trim();
  };

  const canContinue = (() => {
    if (step === 0) return isNewCompany ? companyName.length > 0 : company.accountId !== '';
    if (step === 1)
      return answers.contactFirstName.trim().length > 0 && answers.contactEmail.trim().length > 0;
    return true;
  })();

  const key = STEPS[step]?.key ?? 'company';
  const last = step === STEPS.length - 1;

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title="Create a handover"
      subtitle={`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]?.label ?? ''}`}
      footer={
        <>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
          >
            Back
          </button>
          {/* No save on the last step: the handover goes to the account manager
              by email (the buttons on the review), and they import the client. */}
          {!last && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canContinue}
              onClick={() => setStep((s) => s + 1)}
            >
              Continue
            </button>
          )}
        </>
      }
    >
      {/* Clickable backwards but not forwards: skipping the company step leaves
          the wizard with nothing to attach the onboarding to. */}
      <ol className="onb-steps">
        {STEPS.map((s, i) => (
          <li key={s.key} className={i === step ? 'current' : i < step ? 'done' : ''}>
            <button type="button" disabled={i > step} onClick={() => setStep(i)}>
              <span className="n" aria-hidden="true">
                {i < step ? '✓' : i + 1}
              </span>
              {s.label}
            </button>
          </li>
        ))}
      </ol>

      {options.loading && <Loading label="Loading the form" />}
      {options.error && <ErrorBanner message={options.error} />}

      {key === 'company' && (
        <>
          <p className="sub">
            Who is being handed over. Find the company in Autotask and its address and primary
            contact are filled in for you; company size is the one thing Autotask does not hold.
          </p>

          {source === 'autotask' && (
            <div className="onb-at-pick">
              <Field
                label="Company in Autotask"
                hint={
                  atPicked
                    ? company.accountId === 'new'
                      ? 'Filled in from Autotask. Not yet a Sales account — one is added under this name.'
                      : 'Filled in from Autotask, and matched to the Sales account of the same name.'
                    : 'Type at least two letters of the name.'
                }
              >
                <input
                  className="input"
                  value={atQuery}
                  placeholder="Search Autotask companies"
                  autoFocus
                  onChange={(e) => {
                    setAtQuery(e.target.value);
                    if (atPicked) setAtPicked(null);
                  }}
                />
              </Field>
              {atError && <ErrorBanner message={atError} />}
              {(atSearching || atLoading) && <p className="xs muted">{atLoading ? 'Reading the company…' : 'Searching Autotask…'}</p>}
              {atHits.length > 0 && (
                <ul className="onb-at-hits" role="listbox" aria-label="Autotask companies">
                  {atHits.map((h) => (
                    <li key={h.id}>
                      <button type="button" onClick={() => void pickAutotask(h)}>
                        <span className="strong">{h.name}</span>
                        {(h.city || h.state) && (
                          <span className="xs muted"> · {[h.city, h.state].filter(Boolean).join(', ')}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!atSearching && atQuery.trim().length >= 2 && !atPicked && atHits.length === 0 && !atError && (
                <p className="xs muted">No active Autotask company by that name.</p>
              )}
              <button type="button" className="link-button xs" onClick={() => setSource('accounts')}>
                Not in Autotask? Choose a Sales account or add one
              </button>
            </div>
          )}

          {source === 'accounts' && (
            <>
              <button
                type="button"
                className="link-button xs"
                onClick={() => {
                  setSource('autotask');
                }}
              >
                ← Find the company in Autotask instead
              </button>
              <Field label="Account">
                <select
                  className="select"
                  value={String(company.accountId)}
                  onChange={(e) => chooseAccount(e.target.value)}
                >
                  <option value="">Choose an account</option>
                  {accounts.map((a) => (
                    <option key={a.AccountId} value={a.AccountId}>
                      {a.Name}
                      {a.AccountType === 'client' ? ' (existing client)' : ''}
                    </option>
                  ))}
                  <option value="new">＋ A company not on the list</option>
                </select>
              </Field>
            </>
          )}

          {isNewCompany && (source === 'accounts' || atPicked) && (
            <>
              <Field
                label="Company name"
                hint="Matched against the company list first. A name already there is reused, not duplicated."
              >
                <input
                  className="input"
                  value={company.companyName}
                  onChange={(e) => setC({ companyName: e.target.value })}
                  placeholder="Acme Corporation"
                  autoFocus
                />
              </Field>
              <div className="form-grid">
                <Field label="Industry">
                  <select
                    className="select"
                    value={company.industry}
                    onChange={(e) => setC({ industry: e.target.value })}
                  >
                    <option value="">Not stated</option>
                    {INDUSTRIES.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Website">
                  <input
                    className="input"
                    value={company.website}
                    onChange={(e) => setC({ website: e.target.value })}
                    placeholder="https://example.com"
                  />
                </Field>
              </div>
            </>
          )}

          {!isNewCompany && accountDeals.length > 0 && (
            <Field
              label="The deal this came from"
              hint="Optional. Its MRR is the starting revenue when the client is handed to account management."
            >
              <select
                className="select"
                value={String(company.opportunityId)}
                onChange={(e) =>
                  setC({ opportunityId: e.target.value ? Number(e.target.value) : '' })
                }
              >
                <option value="">No deal recorded</option>
                {accountDeals.map((d) => (
                  <option key={d.OpportunityId} value={d.OpportunityId}>
                    {d.Name} — {formatNumber(d.Mrr, 'currency')}/mo
                  </option>
                ))}
              </select>
            </Field>
          )}

          <LocationFields form={answers} set={setA} />
        </>
      )}

      {key === 'contact' && (
        <>
          <p className="sub">
            Who we deal with while onboarding runs. Copied, not linked: it records who answered on
            the day, and it becomes the primary contact on the account management record at
            handover.
          </p>
          {/* The company's Autotask contacts, when it came from Autotask: the
              primary is filled in already, and any other can be swapped in. */}
          {atPicked && atPicked.contacts.length > 0 && (
            <Field
              label="Contact in Autotask"
              hint={`${atPicked.contacts.length} active contact${atPicked.contacts.length === 1 ? '' : 's'} at ${atPicked.name}. Picking one fills the fields below; you can still edit them.`}
            >
              <select
                className="select"
                value={String(
                  atPicked.contacts.find(
                    (p) =>
                      p.email.toLowerCase() === answers.contactEmail.trim().toLowerCase() &&
                      p.firstName === answers.contactFirstName.trim(),
                  )?.id ?? '',
                )}
                onChange={(e) => {
                  const p = atPicked.contacts.find((x) => String(x.id) === e.target.value);
                  if (!p) return;
                  setA({
                    contactFirstName: p.firstName,
                    contactLastName: p.lastName,
                    contactTitle: p.title,
                    contactEmail: p.email,
                    contactPhone: p.phone || atPicked.phone,
                  });
                }}
              >
                <option value="">Choose a contact</option>
                {atPicked.contacts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || `Contact ${p.id}`}
                    {p.title ? ` — ${p.title}` : ''}
                    {p.primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <ContactFields form={answers} set={setA} autoFocus={!atPicked?.contacts.length} />
        </>
      )}

      {key === 'services' && (
        <>
          <p className="sub">
            What they bought. Each one pulls its own tasks out of the library — the review step
            shows exactly which, before anything is created.
          </p>
          {/* What the Autotask contract says they have. The ticks below stay:
              they decide which onboarding tasks are created. */}
          {atPicked && (
            <div className="card mb-4">
              <div className="card-body tight">
                <p className="eyebrow">On the Autotask contract</p>
                {contractLoading && <p className="xs muted mt-2">Reading {atPicked.name}'s contracts…</p>}
                {contractError && <ErrorBanner message={contractError} />}
                {contract && contract.services.length === 0 && (
                  <p className="small muted mt-2">
                    {contract.contracts.length === 0
                      ? `${atPicked.name} has no active contract in Autotask.`
                      : 'No services on its active contracts.'}
                  </p>
                )}
                {contract && contract.services.length > 0 && (
                  <ContractServiceTable services={contract.services} contracts={contract.contracts} />
                )}
              </div>
            </div>
          )}
          <Field label="Notes" hint="Anything about the services the account manager should know.">
            <textarea
              className="textarea"
              rows={3}
              value={answers.serviceNotes}
              onChange={(e) => setA({ serviceNotes: e.target.value })}
              placeholder="e.g. a legacy line-of-business system that has to be migrated first"
            />
          </Field>
        </>
      )}

      {key === 'technical' && (
        <>
          <p className="sub">
            What they run today. Seats and cloud platform are the two answers that change the work:
            one sizes it, the other adds the tenant configuration.
          </p>
          <TechnicalFields form={answers} set={setA} options={allOptions} />
        </>
      )}

      {key === 'review' && (
        <>
          <div className="card">
            <div className="card-body tight">
              <p className="eyebrow">Onboarding</p>
              <h3 className="mt-2">{companyName || 'No company chosen'}</h3>
              <p className="small muted mt-2">
                {[
                  [answers.contactFirstName, answers.contactLastName].filter(Boolean).join(' '),
                  answers.contactEmail,
                  answers.userCount ? `${answers.userCount} users` : null,
                  answers.timeline ? TIMELINE_LABEL[answers.timeline] : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'No contact details given'}
              </p>
              {answers.services.length > 0 && (
                <p className="small mt-2">
                  {answers.services
                    .map((code) => allOptions.find((s) => s.Code === code)?.Name ?? code)
                    .join(', ')}
                </p>
              )}
            </div>
          </div>

          {contract && contract.services.length > 0 && (
            <div className="card mt-4">
              <div className="card-body tight">
                <p className="eyebrow">On the Autotask contract</p>
                <ContractServiceTable services={contract.services} contracts={contract.contracts} />
              </div>
            </div>
          )}

          <div className="row wrap mt-4" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!companyName}
              onClick={() => {
                const body = handoverEmail();
                void copyText(body).then(setCopied);
                openMail(`Handover: ${companyName}`, body);
              }}
            >
              ✉ Compose email to the account manager
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={!companyName}
              onClick={() => void copyText(handoverEmail()).then(setCopied)}
            >
              {copied ? 'Copied ✓' : 'Copy the handover'}
            </button>
          </div>
          <p className="xs muted mt-1">
            Opens a new email in your mail app with the whole handover in it — add the account manager and send. The text is also
            copied, in case your mail app cuts a long email short.
          </p>
        </>
      )}
    </Drawer>
  );
}

const money = (v: number | null) => (v === null ? '—' : formatNumber(v, 'currency'));

/** The services on the Autotask contract, by contract — the columns of AM's Services tab. */
function ContractServiceTable({ services, contracts }: { services: ContractService[]; contracts: ContractInfo[] }) {
  return (
    <>
      {contracts
        .filter((c) => services.some((s) => s.contractId === c.id))
        .map((c) => (
          <div key={c.id} className="mt-2">
            <div className="small strong">{c.name || 'Contract'}</div>
            <div className="xs muted">
              {[c.number, c.type, `${c.start ?? '—'} – ${c.end ?? 'no end date'}`].filter(Boolean).join(' · ')}
            </div>
            <div className="table-wrap mt-1">
              <table className="table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Period</th>
                    <th className="num">Units</th>
                    <th className="num">Unit price</th>
                    <th className="num">Total</th>
                    <th className="num">Unit cost</th>
                  </tr>
                </thead>
                <tbody>
                  {services
                    .filter((s) => s.contractId === c.id)
                    .map((s) => (
                      <tr key={s.id}>
                        <td>{s.service}</td>
                        <td>{s.period ?? '—'}</td>
                        <td className="num">{s.units === null ? '—' : formatNumber(s.units)}</td>
                        <td className="num">{money(s.unitPrice)}</td>
                        <td className="num strong">{money(s.total)}</td>
                        <td className="num muted">{money(s.unitCost)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </>
  );
}

/** Copies text; true when it worked. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/*
 * A new email in the person's mail app, with no recipient — they add the
 * account manager. A mailto link longer than about 2,000 characters is cut off
 * or refused by some mail apps on Windows, so a long body is shortened with a
 * note to paste the full text, which the button has already copied.
 */
const MAILTO_MAX = 1800;
function openMail(subject: string, body: string) {
  const enc = encodeURIComponent;
  let text = body;
  if (enc(subject).length + enc(text).length > MAILTO_MAX) {
    const note = '\n\n[The full handover is on your clipboard — paste it here.]';
    while (text.length > 200 && enc(subject).length + enc(text + note).length > MAILTO_MAX) {
      text = text.slice(0, Math.floor(text.length * 0.9));
    }
    text = `${text.trimEnd()}…${note}`;
  }
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
}
